import { faTriangleExclamation as faTriangleExclamationRegular } from "@fortawesome/pro-regular-svg-icons/faTriangleExclamation";
import { faQrcode } from "@fortawesome/pro-solid-svg-icons/faQrcode";
import { faSignature } from "@fortawesome/pro-solid-svg-icons/faSignature";
import { faTriangleExclamation } from "@fortawesome/pro-solid-svg-icons/faTriangleExclamation";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Stack, Tab, Typography } from '@mui/material';
import { MprcBarTabs, MprcButton, MprcConfirmDialog, MprcContainer, MprcFileSelection, MprcIconFa, MprcLink, MprcRequestErrorReport, MprcRequestIndicator, MprcSpinnerIndicator } from 'components';
import { ButtonCancel, ButtonSave } from 'components/buttons';
import { MprcNotice } from 'components/MprcNotice';
import { MprcRequestSnackbarErrorReport, MprcResolveRequestErrors } from 'components/MprcRequestErrorReport';
import { MprcSimpleDialog } from 'components/MprcSimpleDialog';
import { FileUploadResultDto, ManagedMeetingDto, MeetingInstructorConflitsResponse, MeetingType, SupervisorClassroomMeetingResponse, SupervisorUpdateClassroomMeetingCommand } from 'generated/CoreApi_v1';
import LibUtils from 'lib/LibUtils';
import MediaUtils from "lib/MediaUtils";
import TzUtils from "lib/TzUtils";
import moment from 'moment';
import { enqueueSnackbar } from 'notistack';
import { useCoreApi } from 'providers/CoreApiProvider';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from "react-router";
import { LmsPaths } from "routes/LmsRouter";
import { getInstructorsConflicts, getMeetingTimezoneId } from 'views/lms/meeting/creation/EditMeeting';
import { EditMeetingInstructorBox } from 'views/lms/meeting/creation/EditMeetingInstructorBox';
import { EditMeetingWhenBox } from 'views/lms/meeting/creation/EditMeetingWhenBox';
import { DoCancelMeeting } from "views/lms/training/Home/EditTraining/Shared/Meeting/DoCancelMeeting";

/////////////////////////////////////////////////////////////////////////////////

export type MeetingPropertiesTab = "location" | "instructors" | "attendance" | "recording";

interface IProps {
    meetingName: string;
    classroomId: string;
    componentId: string;
    openTo: MeetingPropertiesTab;
    onClose: (success?: boolean) => void;
}

interface TabValidation {
    key: MeetingPropertiesTab;
    label: string;
    isValid: boolean;
    enabled: boolean;
}

/////////////////////////////////////////////////////////////////////////////////

export const InstructorEditMeeting: React.FC<IProps> = ({ meetingName, classroomId, componentId, openTo, onClose }) => {

    const api = useCoreApi();
    const { t } = useTranslation("lms");
    const abortControllerRef = useRef<AbortController>(undefined);

    const [dialog, setDialog] = useState<any>();
    const [loading, setLoading] = useState<boolean>(true);
    const [saving, setSaving] = useState<boolean>(false);
    const [currentTab, setCurrentTab] = useState<MeetingPropertiesTab>(openTo);
    const [recordChanged, setRecordChanged] = useState<boolean>(false);
    const [loadDto, setLoadDto] = useState<ManagedMeetingDto>();
    const [viewData, setViewData] = useState<SupervisorClassroomMeetingResponse>();
    const [viewError, setViewError] = useState<any>();
    const [viewFieldsErrors, setViewFieldsErrors] = useState<ApiProblemErrors>();
    const [instructorConflits, setInstructorConflits] = useState<MeetingInstructorConflitsResponse[]>([]);
    const [reactivating, setReactivating] = useState<boolean>(false);
    const [tabs, setTabs] = useState<TabValidation[]>(
        [
            { key: "location", label: t("Meeting.Instructor.LocationTab"), isValid: true, enabled: true },
            { key: "instructors", label: t("Meeting.Common.InstructorsText"), isValid: true, enabled: true },
            { key: "attendance", label: t("Meeting.Instructor.AttendanceTab"), isValid: true, enabled: true },
            { key: "recording", label: t("Meeting.Instructor.RecordingTab"), isValid: true, enabled: true }
        ]
    );

    const disabled = saving || loading;
    const canSave = recordChanged && Object.values(tabs).filter((x) => x.isValid === false).length == 0;
    const canShowCancelButtons = viewData && (viewData.canCancel || viewData.canUndoCancel) && (currentTab == "location" || currentTab == "instructors");
    const canShowEditButtons = viewData && !viewData.hasAttendanceOrScoreAssigned && viewData.canModify && !viewData.isCanceled && (currentTab == "location" || currentTab == "instructors");

    /////////////////////////////////////////////////////////////////////////////////

    ///
    const handleUpdateViewData = (newDto: Partial<ManagedMeetingDto>) => {
        setViewData(state => ({ ...state!, meeting: { ...viewData!.meeting, ...newDto } }));
    };

    ///
    const handleUpdateTab = (key: MeetingPropertiesTab, tab: Partial<TabValidation>) => {
        setTabs(state => (state.map(x => x.key == key ? { ...x, ...tab } : x)));
    };

    ///
    const resolveErrors = (e: any | unknown) => {
        var r = MprcResolveRequestErrors(e);
        if (r.fieldsWithError) {
            setViewFieldsErrors(r.fieldsWithError);
            setViewError(r.otherErrors);
            enqueueSnackbar(t("Record.SaveFailedWithFieldErrorsMessage", { ns: "shared" }), { variant: "error" });
        } else {
            setViewError(e);
        }
    };

    ///
    const handleCancel = () => {
        setDialog(
            <DoCancelMeeting
                classroomId={classroomId}
                componentId={componentId}
                onClose={(success) => {
                    onClose(success);
                    setDialog(null);
                }}
            />
        );
    };

    ///
    const handleUndoCancel = () => {

        if (viewData?.canModify === true) {
            enqueueSnackbar(t("Meeting.Instructor.UndoCancelWarningMessage"), { variant: "warning" });
            setViewData({ ...viewData!, isCanceled: false, canCancel: false, canUndoCancel: false });
            setReactivating(true);
        }
        else {
            setSaving(true);
            api.v1.lmsMonitoring.instructorUndoCancelMeeting({ classroomId, componentId })
                .then(({ data }) => {
                    if (data) {
                        enqueueSnackbar(t("Meeting.Instructor.UndoCancelSuccessMessage"), { variant: "success" });
                        setLoading(true);
                    } else {
                        enqueueSnackbar(t("Meeting.Instructor.UndoCancelErrorMessage"), { variant: "error" });
                    }
                }).catch(e => MprcRequestSnackbarErrorReport(e))
                .finally(() => setSaving(false));
        }
    };

    ///
    const handlePublishRecording = (file: FileUploadResultDto | null) => {

        const isRemovingFile = file == null;
        const recordingBefore = viewData?.recordingFile ?? null;

        if (!isRemovingFile) setViewData({ ...viewData!, recordingFile: file });

        setSaving(true);
        api.v1.lmsMonitoring.instructorPublishMeetingRecording({ classroomId, componentId, recordingFileId: file?.fileId ?? null })
            .then(({ data }) => {
                if (data) {
                    enqueueSnackbar(file ? t("Meeting.Instructor.PublishRecordingSuccessMessage") : t("Meeting.Instructor.DeleteRecordingSuccessMessage"), { variant: "success" });
                    setViewData({ ...viewData!, recordingFile: file });
                }
                else {
                    enqueueSnackbar(file ? t("Meeting.Instructor.PublishRecordingErrorMessage") : t("Meeting.Instructor.DeleteRecordingErrorMessage"), { variant: "error" });
                    setViewData({ ...viewData!, recordingFile: recordingBefore });
                }
            })
            .catch(e => {
                MprcRequestSnackbarErrorReport(e);
                setViewData({ ...viewData!, recordingFile: recordingBefore });
            })
            .finally(() => setSaving(false));
    };

    ///
    const handleDeleteRecording = () => {
        setDialog(<MprcConfirmDialog
            open
            message={t("Meeting.Instructor.ConfirmDeleteRecordingMessage")}
            onClose={(yes) => {
                if (yes) {
                    handlePublishRecording(null);
                }
                setDialog(null);
            }}
        />
        );
    };

    ///
    const buildPostDto = (dto: ManagedMeetingDto): SupervisorUpdateClassroomMeetingCommand => {

        var timezoneId: string | null = getMeetingTimezoneId(dto);

        var request: SupervisorUpdateClassroomMeetingCommand = {
            classroomId,
            componentId,
            instructors: dto.instructors.length == 0 ? null : dto.instructors.map(x => x.key),
            assistants: dto.assistants.length == 0 ? null : dto.assistants.map(x => x.key),
            meetingType: dto.meetingType,
            startDate: timezoneId == null ? dto.startDate : TzUtils.applyTimezoneOffsetToUtc(dto.startDate, timezoneId),
            endDate: timezoneId == null ? dto.endDate : TzUtils.applyTimezoneOffsetToUtc(dto.endDate, timezoneId),
            bookAdditionalTimeAfter: moment(dto.bookAdditionalTimeAfter, "HH:mm").isValid() ? dto.bookAdditionalTimeAfter : null,
            bookAdditionalTimeBefore: moment(dto.bookAdditionalTimeBefore, "HH:mm").isValid() ? dto.bookAdditionalTimeBefore : null
        } as SupervisorUpdateClassroomMeetingCommand;

        if (dto.meetingType == MeetingType.Presential) {
            request.address = !dto.meetingRoom ? dto.location?.address ?? null : null;
            request.locationName = !dto.meetingRoom ? dto.location?.locationName ?? null : null;
            request.roomId = dto.meetingRoom?.key;
        }
        else {
            if (dto.onlineLocation) {
                request.timezoneId = dto.onlineLocation.timezoneId;
                request.externalUrl = dto.onlineLocation.externalUrl;
            }
        }

        return request;
    };

    ///
    const handleSave = () => {
        setSaving(true);
        api.v1.lmsMonitoring.instructorUpdateMeeeting(buildPostDto(viewData!.meeting))
            .then(({ data: success }) => {
                if (success) {
                    enqueueSnackbar(t("Meeting.Common.UpdateSuccessMessage"), { variant: "success" });
                    onClose(success);
                }
                else {
                    enqueueSnackbar(t("Meeting.Common.UpdateErrorMessage"), { variant: "error" });
                }
            }).catch(e => MprcRequestSnackbarErrorReport(e))
            .finally(() => setSaving(false));
    };


    ////////////////////////////////////////////////////////////////////////////
    /// EFFECTS
    ////////////////////////////////////////////////////////////////////////////

    useEffect(() => {
        async function getData() {
            try {

                var result = await api.v1.lmsMonitoring.getSupervisorClassroomMeeting({ componentId, classroomId });

                const { data } = result;
                const timezoneId = getMeetingTimezoneId(data.meeting);
                if (timezoneId != null) {
                    data.meeting.startDate = TzUtils.applyTimezoneOffsetFromUtc(data.meeting.startDate, timezoneId);
                    data.meeting.endDate = TzUtils.applyTimezoneOffsetFromUtc(data.meeting.endDate, timezoneId);
                }

                setLoadDto(LibUtils.deepCopy(data.meeting));
                setViewData(result.data);
                handleUpdateTab("recording", { enabled: result.data.canPublishRecording });
                handleUpdateTab("attendance", { enabled: result.data.canAccessAttendanceList });
            }
            catch (e) {
                setViewError(e);
            }
            finally {
                setLoading(false);
            }
        }

        if (loading) getData();
    }, [loading]);

    useEffect(() => {
        if (viewData) {
            const isRecordChanged = !LibUtils.deepCompare(viewData.meeting, loadDto);
            setRecordChanged(isRecordChanged || reactivating);

            if (viewData.meeting.startDate != loadDto?.startDate
                || viewData.meeting.endDate != loadDto?.endDate
                || !LibUtils.deepCompare(viewData.meeting.instructors, loadDto?.instructors)
                || viewData.meeting.bookAdditionalTimeAfter != loadDto?.bookAdditionalTimeAfter
                || viewData.meeting.bookAdditionalTimeBefore != loadDto?.bookAdditionalTimeBefore) {

                abortControllerRef.current?.abort();
                abortControllerRef.current = new AbortController();
                getInstructorsConflicts(api, viewData.meeting, abortControllerRef.current.signal, (e) => setViewError(e)).then(conflicts => setInstructorConflits(conflicts));
            }
        }
    }, [viewData]);


    ////////////////////////////////////////////////////////////////////////////

    const RenderNotice = (
        <MprcNotice
            items={[{
                text: t("Meeting.MainBox.CloseEnrollmentsMessage"),
                icon: faTriangleExclamationRegular,
                iconColor: "warning",
            }]}
        />
    );

    const RenderAttendanceAssignedNotice = (
        <MprcNotice
            items={[{
                text:
                    <>
                        {t("Meeting.Instructor.AttendanceAssignedWarningMessageL1")} <br />
                        {t("Meeting.Instructor.AttendanceAssignedWarningMessageL2")}
                    </>,
                icon: faTriangleExclamationRegular,
                iconColor: "warning",
                action: <MprcLink
                    onClick={
                        () => {
                            setViewData({ ...viewData!, hasAttendanceOrScoreAssigned: false });
                        }
                    }
                >
                    {t("Meeting.Instructor.EnableEditionButtonText")}
                </MprcLink>
            }]}
        />
    );

    const RenderNotifyModificationNotice =
        (
            <MprcNotice
                items={[{ text: t("Meeting.Instructor.NotifyModificationWarningMessage") }]}
            />
        );


    return <MprcSimpleDialog
        title={t("Meeting.Instructor.Title")}
        subtitle={meetingName}
        message={
            <Box>
                {dialog}
                {loading && <MprcSpinnerIndicator />}
                <MprcRequestIndicator visible={saving} type={"post"} />

                {
                    viewError ?
                        <MprcRequestErrorReport error={viewError} />
                        : viewData && viewData.isCanceled
                            ? (
                                <MprcNotice
                                    items={[{
                                        text: t("Meeting.Instructor.CanceledWarningMessage"),
                                        icon: faTriangleExclamationRegular,
                                        iconColor: "error"
                                    }]}
                                />
                            )
                            : viewData &&
                            (
                                <>
                                    <MprcBarTabs sx={{ marginBottom: 1 }}
                                        variant="fullWidth"
                                        value={currentTab}
                                        onChange={(v, selected) => setCurrentTab(selected)}>
                                        {tabs.filter(p => p.enabled).map(item =>
                                            <Tab
                                                key={item.key}
                                                value={item.key}
                                                label={item.label}
                                                icon={
                                                    !item.isValid ?
                                                        <MprcIconFa
                                                            icon={faTriangleExclamation}
                                                            color="warning"
                                                            fixedWidth
                                                            title={t("Meeting.Common.AlertInvalidValuesTooltip")}
                                                        />
                                                        :
                                                        <></>
                                                }
                                                iconPosition="end"
                                            />
                                        )}
                                    </MprcBarTabs>
                                    {(currentTab == "location" || currentTab == "instructors") &&
                                        <>
                                            {
                                                !canShowEditButtons
                                                    ? RenderAttendanceAssignedNotice
                                                    : viewData.hasCloseEnrollsments
                                                        ? RenderNotice
                                                        : viewData.canModify === true && RenderNotifyModificationNotice
                                            }
                                        </>
                                    }
                                    <EditMeetingWhenBox
                                        mode={'classroom'}
                                        visible={currentTab == "location"}
                                        viewData={
                                            {
                                                endDate: viewData.meeting.endDate,
                                                location: viewData.meeting.location,
                                                meetingRoom: viewData.meeting.meetingRoom,
                                                meetingType: viewData.meeting.meetingType,
                                                meetingTypesEnabled: viewData.meeting.meetingTypesEnabled,
                                                multipleClasses: viewData.meeting.multipleClasses,
                                                onlineLocation: viewData.meeting.onlineLocation,
                                                startDate: viewData.meeting.startDate,
                                            }
                                        }

                                        viewErrors={viewFieldsErrors}
                                        onFormUpdate={handleUpdateViewData}
                                        onFormValidation={(valid, hasError) => handleUpdateTab("location", { isValid: valid && !hasError })}
                                        onError={e => resolveErrors(e)}
                                        readonly={!canShowEditButtons}
                                    />
                                    <EditMeetingInstructorBox
                                        mode={'classroom'}
                                        visible={currentTab == "instructors"}
                                        viewData={
                                            {
                                                assistants: viewData.meeting.assistants,
                                                bookAdditionalTimeAfter: viewData.meeting.bookAdditionalTimeAfter,
                                                bookAdditionalTimeBefore: viewData.meeting.bookAdditionalTimeBefore,
                                                endDate: viewData.meeting.endDate,
                                                instructors: viewData.meeting.instructors,
                                                meetingType: viewData.meeting.meetingType,
                                                multipleClasses: viewData.meeting.multipleClasses,
                                                startDate: viewData.meeting.startDate,
                                                instructorConflits
                                            }
                                        }
                                        viewErrors={viewFieldsErrors}
                                        onFormUpdate={handleUpdateViewData}
                                        onFormValidation={(valid, hasError) => handleUpdateTab("instructors", { isValid: valid && !hasError })}
                                        onError={e => resolveErrors(e)}
                                        readonly={!canShowEditButtons}
                                    />
                                    {viewData && currentTab == "attendance" &&
                                        <>
                                            <MprcContainer>
                                                <Typography variant="overline" component="p">{t("Meeting.Instructor.RegisterAttendanceSubtitle")}</Typography>
                                                <Typography variant="caption">{t("Meeting.Instructor.RegisterAttendanceHint")}</Typography>
                                                <Stack direction="row" spacing={5}>
                                                    <Link to={LmsPaths.BuildSupervisorMeetingQrCode(classroomId, componentId)} target="_blank">
                                                        <MprcButton>
                                                            <Box sx={{ mr: 1 }}><FontAwesomeIcon icon={faQrcode} size="3x" /></Box>
                                                            {t("Meeting.Instructor.QrcodeButtonText")}
                                                        </MprcButton>
                                                    </Link>
                                                    <Link to={LmsPaths.BuildSupervisorMeetingAttendanceList(classroomId, componentId)} target="_blank">
                                                        <MprcButton>
                                                            <Box sx={{ mr: 1 }}><FontAwesomeIcon icon={faSignature} size="3x" /></Box>
                                                            {t("Meeting.Instructor.SignatureListButtonText")}
                                                        </MprcButton>
                                                    </Link>
                                                </Stack>
                                            </MprcContainer>
                                            <Typography variant="overline" component="p">Registro Manual de Presença e Nota</Typography>
                                            <Typography variant="caption">{t("Meeting.Instructor.RegisterAttendanceManualHint")}</Typography>
                                        </>
                                    }
                                    {viewData && currentTab == "recording" && viewData.canPublishRecording &&
                                        <>
                                            <MprcContainer>
                                                <Typography variant="overline" component="p">{t("Meeting.Instructor.RecordingUploadSubtitle")}</Typography>
                                                <Typography variant="caption">{t("Meeting.Instructor.RecordingUploadHint")}</Typography>
                                            </MprcContainer>
                                            <MprcFileSelection
                                                acceptedFiles={MediaUtils.DropzoneAcceptVideos()}
                                                selectedFile={viewData.recordingFile}
                                                onFileUploadSuccess={(file) => handlePublishRecording(file.mainFile)}
                                                onFileUploadStarted={() => setSaving(true)}
                                                onFileUploadError={() => setSaving(false)}
                                                onClear={() => handleDeleteRecording()}
                                            />
                                        </>
                                    }
                                </>
                            )
                }
            </Box>
        }
        actions={
            <>
                {
                    canShowCancelButtons
                        ? viewData!.canCancel
                            ? <ButtonCancel
                                color="error"
                                text={t("Meeting.Instructor.CancelButtonText")}
                                disabled={disabled}
                                variant="contained"
                                onClick={() => handleCancel()}
                            />
                            : viewData!.canUndoCancel &&
                            <ButtonCancel
                                color="error"
                                text={t("Meeting.Instructor.UndoCancelButtonText")}
                                disabled={disabled}
                                variant="contained"
                                onClick={() => handleUndoCancel()}
                            />

                        : <div></div>
                }

                {
                    canShowEditButtons ?
                        <Stack direction="row" spacing={1}>
                            <ButtonCancel
                                disabled={disabled}
                                variant="contained"
                                onClick={() => onClose()}
                            />
                            <ButtonSave
                                disabled={disabled || !canSave}
                                variant="contained"
                                onClick={() => handleSave()}
                            />
                        </Stack>
                        :
                        <MprcButton
                            variant="contained"
                            disabled={disabled}
                            onClick={() => {
                                if (recordChanged) {
                                    setDialog(
                                        <MprcConfirmDialog
                                            open
                                            message={t("Meeting.Instructor.PendingChangesMessage")}
                                            onClose={(yes) => {
                                                if (yes) onClose();
                                                else setCurrentTab("location");
                                                setDialog(null);
                                            }}
                                        />
                                    );
                                }
                                else {
                                    onClose();
                                }
                            }}
                        >
                            Fechar
                        </MprcButton>
                }
            </>
        }
        actionsPosition={canShowCancelButtons ? "space-between" : "end"}
        onClose={onClose}
    />;
};
